import {
  FavoriteLegislation,
  FavoriteWorkspaceDocument,
  UsersFavoriteDocuments,
  User,
} from "@/models/users/User";
import {
  CrudServiceType,
  crudService,
  handlePaginationQuery,
} from "./crudService";
import { RestClient } from "./restClient";
import RequestMethod from "@/models/client/RequestMethod";
import RestResponse, { RestSuccessResponse } from "@/models/client/RestResponse";
import useUserStore from "@/stores/user.store";
import {
  FavoriteLegislationRequest,
  FavoriteWorkspaceDocumentRequest,
  RemoveFavoriteLegislationRequest,
  RemoveFavoriteWorkspaceDocumentRequest,
  RenameFavoriteLegislationRequest,
  RenameFavoriteWorkspaceDocumentRequest,
} from "@/models/users/User";
import { Pagination, PaginationQuery } from "@/models/base/Pagination";
import {
  Subscription,
  SubscriptionRequest,
} from "@/models/subscriptions/Subscription";
import {
  LegislationTimelineCheckResponse,
  LegislationSubscription,
  LegislationSubscriptionCreateRequest,
  LegislationSubscriptionRemoveRequest,
  LegislationSubscriptionUpdateRequest,
} from "@/models/subscriptions/LegislationSubscription.ts";
import { UsersNotificationSubscriptionStatus } from "@/models/notification/Notifications";
import { NotificationPagination } from "@/models/notification/NotificationPagination";
import { Notification } from "@/models/notification/Notifications";

export type UserServiceType = CrudServiceType<User> & {
  getMe(): RestResponse<User>;
  getAllTenantUser(): RestResponse<User[]>;
  onLogin(): void; // Fire and forget
  getUsersFavoriteDocuments(): RestResponse<UsersFavoriteDocuments>;
  addFavoriteLegislation(
    request: FavoriteLegislationRequest
  ): RestResponse<FavoriteLegislation[]>;
  addFavoriteWorkspaceDocument(
    request: FavoriteWorkspaceDocumentRequest
  ): RestResponse<FavoriteWorkspaceDocument[]>;
  removeFavoriteLegislation(
    request: RemoveFavoriteLegislationRequest
  ): RestResponse<FavoriteLegislation[]>;
  removeFavoriteWorkspaceDocument(
    request: RemoveFavoriteWorkspaceDocumentRequest
  ): RestResponse<FavoriteWorkspaceDocument[]>;
  renameFavoriteLegislation(
    request: RenameFavoriteLegislationRequest
  ): RestResponse<FavoriteLegislation[]>;
  renameFavoriteWorkspaceDocument(
    request: RenameFavoriteWorkspaceDocumentRequest
  ): RestResponse<FavoriteWorkspaceDocument[]>;
  reorderFavoriteDocuments(orderedIds: string[]): RestResponse<string[]>;
  reorderFavoriteSearches(orderedIds: string[]): RestResponse<string[]>;
  reorderSubscriptions(orderedIds: string[]): RestResponse<string[]>;
  getDetailedSubscriptions(
    pageNumber: number,
    pageSize?: number
  ): RestResponse<Pagination<Subscription>>;
  getSubscriptions(): RestResponse<Subscription[]>;
  subscribe(subscribe: SubscriptionRequest): RestResponse<Subscription>;
  unsubscribe(subscribe: SubscriptionRequest): RestResponse<void>;
  createLegislationSubscription(
    request: LegislationSubscriptionCreateRequest
  ): RestResponse<LegislationSubscription>;
  updateLegislationSubscription(
    request: LegislationSubscriptionUpdateRequest
  ): RestResponse<LegislationSubscription>;
  removeLegislationSubscription(
    request: LegislationSubscriptionRemoveRequest
  ): RestResponse<void>;
  checkLegislationTimelineSubscription(
    externalId: string,
    datasource: string
  ): RestResponse<LegislationTimelineCheckResponse>;
  getAllNotifications(
    query: PaginationQuery
  ): RestResponse<Pagination<Notification>>;
  getActiveAndSeenNotifications(
    query: PaginationQuery
  ): RestResponse<NotificationPagination>;
  markAllNotificationsAsSeen(): RestResponse<boolean>;
  markNotificationsAsSeen(notificationIds: string[]): RestResponse<boolean>;
  dismissNotification(notificationId: string): RestResponse<boolean>;
  dismissNotifications(notificationIds: string[]): RestResponse<boolean>;
  dismissAllNotifications(): RestResponse<boolean>;
  deleteNotifications(notificationIds: string[]): RestResponse<boolean>;
  deleteAllNotifications(): RestResponse<boolean>;
};

const userServices = (client: RestClient, path: string): UserServiceType => ({
  ...crudService<User>(client, path),
  getMe: async () => {
    const response = await client.fetch<User>(RequestMethod.GET, `${path}/me`);
    if (response.success) {
      useUserStore.setState({ me: response.value });
    }
    return response;
  },
  getAllTenantUser: async () => {
    const response = await client.fetch<User[]>(
      RequestMethod.GET,
      `${path}/all-tenant-users`
    );
    return response;
  },
  onLogin: () => {
    client.fetch<User>(RequestMethod.PUT, `${path}/bump-login-count`);
  },
  getUsersFavoriteDocuments: async () => {
    const response = await client.fetch<UsersFavoriteDocuments>(
      RequestMethod.GET,
      `${path}/favorite-documents`
    );
    return response;
  },
  addFavoriteLegislation: async (request: FavoriteLegislationRequest) => {
    const response = await client.fetch<FavoriteLegislation[]>(
      RequestMethod.PUT,
      `${path}/favorite-documents/add-legislation`,
      request
    );
    return response;
  },
  addFavoriteWorkspaceDocument: async (
    request: FavoriteWorkspaceDocumentRequest
  ) => {
    const response = await client.fetch<FavoriteWorkspaceDocument[]>(
      RequestMethod.PUT,
      `${path}/favorite-documents/add-workspace-document`,
      request
    );
    return response;
  },
  removeFavoriteLegislation: async (
    request: RemoveFavoriteLegislationRequest
  ) => {
    const response = await client.fetch<FavoriteLegislation[]>(
      RequestMethod.PUT,
      `${path}/favorite-documents/remove-legislation`,
      request
    );
    return response;
  },
  removeFavoriteWorkspaceDocument: async (
    request: RemoveFavoriteWorkspaceDocumentRequest
  ) => {
    const response = await client.fetch<FavoriteWorkspaceDocument[]>(
      RequestMethod.PUT,
      `${path}/favorite-documents/remove-workspace-document`,
      request
    );
    return response;
  },
  renameFavoriteLegislation: async (request: RenameFavoriteLegislationRequest) => {
    const response = await client.fetch<FavoriteLegislation[]>(
      RequestMethod.PUT,
      `${path}/favorite-documents/rename-legislation`,
      request
    );
    return response;
  },
  renameFavoriteWorkspaceDocument: async (
    request: RenameFavoriteWorkspaceDocumentRequest
  ) => {
    const response = await client.fetch<FavoriteWorkspaceDocument[]>(
      RequestMethod.PUT,
      `${path}/favorite-documents/rename-workspace-document`,
      request
    );
    return response;
  },
  reorderFavoriteDocuments: async (orderedIds: string[]) => {
    return await client.fetch<string[]>(
      RequestMethod.PUT,
      `${path}/favorite-documents/reorder`,
      { orderedIds }
    );
  },
  reorderFavoriteSearches: async (orderedIds: string[]) => {
    return await client.fetch<string[]>(
      RequestMethod.PUT,
      `${path}/favorite-searches/reorder`,
      { orderedIds }
    );
  },
  reorderSubscriptions: async (orderedIds: string[]) => {
    return await client.fetch<string[]>(
      RequestMethod.PUT,
      `${path}/subscriptions/reorder`,
      { orderedIds }
    );
  },
  getDetailedSubscriptions: async (pageNumber: number, pageSize = 20) => {
    const response = await client.fetch<Pagination<Subscription>>(
      RequestMethod.GET,
      `${path}/me/subscriptions?pageNumber=${pageNumber}&pageSize=${pageSize}`
    );
    return response;
  },
  getSubscriptions: async () => {
    const response = await client.fetch<Subscription[]>(
      RequestMethod.GET,
      `${path}/subscriptions/simple`
    );
    return response;
  },
  subscribe: async (subscribe: SubscriptionRequest) => {
    const response = await client.fetch<Subscription>(
      RequestMethod.POST,
      `${path}/subscription`,
      subscribe
    );
    return response;
  },
  unsubscribe: async (subscribe: SubscriptionRequest) => {
    const response = await client.fetch<void>(
      RequestMethod.DELETE,
      `${path}/subscription`,
      subscribe
    );
    return response;
  },
  createLegislationSubscription: async (
    request: LegislationSubscriptionCreateRequest
  ) => {
    return client.fetch<LegislationSubscription>(
      RequestMethod.POST,
      `${path}/legislation-subscription`,
      request
    );
  },
  updateLegislationSubscription: async (
    request: LegislationSubscriptionUpdateRequest
  ) => {
    return client.fetch<LegislationSubscription>(
      RequestMethod.PUT,
      `${path}/legislation-subscription`,
      request
    );
  },
  removeLegislationSubscription: async (
    request: LegislationSubscriptionRemoveRequest
  ) => {
    return client.fetch<void>(
      RequestMethod.DELETE,
      `${path}/legislation-subscription`,
      request
    );
  },
  checkLegislationTimelineSubscription: async (
    externalId: string,
    datasource: string
  ) => {
    const params = new URLSearchParams({
      externalId,
      datasource,
    });
    return client.fetch<LegislationTimelineCheckResponse>(
      RequestMethod.GET,
      `${path}/legislation-subscription/timeline-check?${params.toString()}`
    );
  },
  getAllNotifications: async (query: PaginationQuery) => {
    const response = await client.fetch<Pagination<Notification>>(
      RequestMethod.GET,
      `${path}/me/notifications?${handlePaginationQuery(query)}`
    );
    return response;
  },
  getActiveAndSeenNotifications: async (query: PaginationQuery) => {
    const response = await client.fetch<NotificationPagination>(
      RequestMethod.GET,
      `${path}/me/notifications?status=${
        UsersNotificationSubscriptionStatus.Active
      }&status=${
        UsersNotificationSubscriptionStatus.Seen
      }&${handlePaginationQuery(query)}`
    );
    return response;
  },
  markAllNotificationsAsSeen: async () => {
    const response = await client.fetch<boolean>(
      RequestMethod.PUT,
      `${path}/me/notifications/mark-all-as-seen`
    );
    return response;
  },
  markNotificationsAsSeen: async (notificationIds: string[]) => {
    const response = await client.fetch<boolean>(
      RequestMethod.PUT,
      `${path}/me/notifications/mark-as-seen`,
      notificationIds
    );
    return response;
  },
  dismissNotification: async (notificationId: string) => {
    const response = await client.fetch<boolean>(
      RequestMethod.PUT,
      `${path}/notifications/dismiss`,
      [notificationId]
    );
    return response;
  },
  dismissNotifications: async (notificationIds: string[]) => {
    if (notificationIds.length === 0) {
      return Promise.resolve({
        success: true as const,
        status: 200,
        statusText: "",
        feedback: { severity: "success" as const, message: "" },
        value: true,
      } satisfies RestSuccessResponse<boolean>);
    }
    const response = await client.fetch<boolean>(
      RequestMethod.PUT,
      `${path}/notifications/dismiss`,
      notificationIds
    );
    return response;
  },
  dismissAllNotifications: async () => {
    const response = await client.fetch<boolean>(
      RequestMethod.PUT,
      `${path}/notifications/dismiss-all`
    );
    return response;
  },
  deleteNotifications: async (notificationIds: string[]) => {
    const response = await client.fetch<boolean>(
      RequestMethod.DELETE,
      `${path}/notifications`,
      notificationIds
    );
    return response;
  },
  deleteAllNotifications: async () => {
    const response = await client.fetch<boolean>(
      RequestMethod.DELETE,
      `${path}/notifications/all`
    );
    return response;
  },
});

export default userServices;
